import { createSelector } from 'reselect';
import viewerSessionsSelector from '@store/viewerSessions/selectors';
import compareDashbooardSelector from '@store/comparison/selectors';
import errorDashboardSelector from '@store/errorDiagnostics/selectors';
import {
  intervalPayloadSelector,
  birdChartIntervalSelector,
} from '@store/errorDiagnostics/base/selectors';
import get from 'lodash/get';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import { getRulesFromFilter, getFilterFromRules, granularity as tGranularity } from '@utils';
import { DATASET } from '@utils/constants';
import viewerTimelineRequestSelector from '../timeline/request/selectors';

export const compareDashboardExportMetaSelector = createSelector(
  compareDashbooardSelector,
  (compareDashboardStore) => {
    const requests = get(compareDashboardStore, 'requests');
    const filterMetaList = get(compareDashboardStore, 'compareData.filterMetaList');
    const firstPayload = get(requests, '0.queries.0');
    const queries = requests.map((request, idx) => {
      const payload = get(request, 'queries.0');
      const [from, to] = payload.interval.split('/');
      const tempRules = getRulesFromFilter(payload.filter, true);
      const filter = getFilterFromRules(tempRules, true);
      filter.name = filterMetaList[idx].name.replace(/__/g, ' ');
      return {
        filter,
        granularity: tGranularity(from, to, true),
      };
    });
    return {
      dashboardType: 'compare',
      dataset: DATASET,
      queryType: 'timeseries',
      interval: firstPayload.interval,
      metrics: firstPayload.metrics,
      queries,
    };
  },
);

export const compareWidgetMetaSelector = createSelector(
  compareDashbooardSelector,
  (compareDashboardStore) => {
    const store = cloneDeep(compareDashboardStore);
    let requests = get(store, 'requests');
    const exportedMetric = get(compareDashboardStore, 'exportedMetric');
    requests = requests.map((request) => {
      const metrics = get(request, 'queries.0.metrics');
      const exportedMetrics = metrics.filter((metric) => {
        const regexp = new RegExp(metric, 'i');
        return regexp.test(exportedMetric);
      });
      set(request, 'queries.0.metrics', exportedMetrics);
      return request;
    });

    const filterMetaList = get(compareDashboardStore, 'compareData.filterMetaList');
    const firstPayload = get(requests, '0.queries.0');
    const queries = requests.map((request, idx) => {
      const payload = get(request, 'queries.0');
      const [from, to] = payload.interval.split('/');
      const tempRules = getRulesFromFilter(payload.filter, true);
      const filter = getFilterFromRules(tempRules, true);
      filter.name = filterMetaList[idx].name.replace(/__/g, ' ');
      return {
        filter,
        granularity: tGranularity(from, to, true),
      };
    });
    return {
      dataset: DATASET,
      dashboardType: 'compare',
      queryType: 'timeseries',
      interval: firstPayload.interval,
      metrics: firstPayload.metrics,
      queries,
      widget: 'comparison',
    };
  },
);

export const viewerTimelineMetaSelector = createSelector(
  viewerTimelineRequestSelector,
  (timelineRequest) => ({
    dashboardType: 'timeline',
    dataset: DATASET,
    timeline: {
      queries: timelineRequest,
      queryType: 'timeline',
    },
  }),
);

export const errorDashboardExportMetaSelector = createSelector(
  errorDashboardSelector,
  viewerSessionsSelector,
  intervalPayloadSelector,
  birdChartIntervalSelector,
  (errorDashboardStore, viewerSessionsStore, intervalObj, birdChartIntervalStore) => {
    const dashboardFilter = get(errorDashboardStore, 'base.filter');
    dashboardFilter.name = dashboardFilter.name.replace(/__/g, ' ');
    const activeMetric = get(errorDashboardStore, 'base.metric');
    const errorCode = get(errorDashboardStore, 'base.currentErrorCode');
    const birdChartPayload = get(errorDashboardStore, 'birdChart.query.queries.0');
    const barChartsPayload = get(errorDashboardStore, 'barChart.payload');

    const barChartQueries = barChartsPayload.map((payload) => {
      const query = get(payload, 'queries.0');
      const { metrics, interval, filter, granularity } = query;
      const rules = getRulesFromFilter(filter);
      if (
        Object.values(rules).some(
          (ruleObj) => ruleObj.field.endsWith('_ERROR') && ruleObj.value !== errorCode,
        )
      ) {
        return null;
      }
      return {
        interval,
        filter,
        granularity,
        metrics,
      };
    });

    const errorTableQuery = get(errorDashboardStore, 'errorTable.payload.queries.0');
    const errorTableMeta = {
      interval: errorTableQuery.interval,
      granularity: errorTableQuery.granularity,
      metrics: errorTableQuery.metrics,
      filter: errorTableQuery.filter,
      groupBy: errorTableQuery.groupBy,
      sortBy: errorTableQuery.sortBy,
      orderBy: errorTableQuery.orderBy,
    };

    const dimensionTablesPayload = get(errorDashboardStore, 'dimensionTables.requests');
    const dimensionNames = get(errorDashboardStore, 'dimensionTables.core.dimensions');
    const dimensionTableQueries = dimensionTablesPayload.map((payload, index) => {
      const query = get(payload, 'payload.queries.0');
      return {
        interval: query.interval,
        granularity: query.granularity,
        metrics: query.metrics,
        filter: query.filter,
        groupBy: query.groupBy,
        sortBy: query.sortBy,
        orderBy: query.orderBy,
        dimensionName: dimensionNames[index],
      };
    });

    const viewerSessionsVisible = viewerSessionsStore[`${errorCode}`].dataType === 'sessions';
    const viewerSessionQuery = get(viewerSessionsStore, 'trends.query');

    return {
      dashboardType: 'error',
      dataset: DATASET,
      filter: dashboardFilter,
      activeMetric,
      errorCode,
      birdChart: {
        queryType: 'timeseries',
        queries: {
          interval: birdChartIntervalStore.interval,
          granularity: birdChartPayload.granularity,
          metrics: birdChartPayload.metrics,
          filter: birdChartPayload.filter,
        },
      },
      barChart: {
        queryType: 'timeseries',
        queries: barChartQueries.filter((query) => query !== null),
      },
      errorTable: {
        queryType: 'group-by',
        queries: errorTableMeta,
      },
      dimensionTables: {
        visible: !viewerSessionsVisible,
        queryType: 'group-by',
        queries: dimensionTableQueries,
      },
      viewerSessions: {
        visible: viewerSessionsVisible,
        queryType: 'scan',
        queries: viewerSessionQuery,
      },
    };
  },
);

export const errorBirdChartMetaSelector = createSelector(
  errorDashboardSelector,
  intervalPayloadSelector,
  birdChartIntervalSelector,
  (errorDashboardStore, intervalObj, birdChartIntervalStore) => {
    const birdChartPayload = get(errorDashboardStore, 'birdChart.query.queries.0');

    return {
      dataset: DATASET,
      filter: get(errorDashboardStore, 'base.filter'),
      errorCode: get(errorDashboardStore, 'base.currentErrorCode'),
      widget: 'error_bird_chart',
      birdChart: {
        queryType: 'timeseries',
        queries: {
          interval: birdChartIntervalStore.interval,
          granularity: birdChartPayload.granularity,
          metrics: birdChartPayload.metrics,
          filter: birdChartPayload.filter,
        },
      },
    };
  },
);

export const errorBarChartMetaSelector = createSelector(
  errorDashboardSelector,
  (errorDashboardStore) => {
    const barChartsPayload = get(errorDashboardStore, 'barChart.payload');
    const errorCode = get(errorDashboardStore, 'base.currentErrorCode');
    const barChartQueries = barChartsPayload.map((payload) => {
      const query = get(payload, 'queries.0');
      const { metrics, interval, filter, granularity } = query;
      const rules = getRulesFromFilter(filter);
      if (
        Object.values(rules).some(
          (ruleObj) => ruleObj.field.endsWith('_ERROR') && ruleObj.value !== errorCode,
        )
      ) {
        return null;
      }
      return {
        interval,
        filter,
        granularity,
        metrics,
      };
    });
    return {
      dataset: DATASET,
      barChart: {
        queryType: 'timeseries',
        queries: barChartQueries.filter((query) => query !== null),
      },
      widget: 'error_bar_chart',
      errorCode,
    };
  },
);

export const errorTopTrendsMetaSelector = createSelector(
  errorDashboardSelector,
  (errorDashboardStore) => {
    const errorTableQuery = get(errorDashboardStore, 'errorTable.payload.queries.0');
    const errorTableMeta = {
      interval: errorTableQuery.interval,
      granularity: errorTableQuery.granularity,
      metrics: errorTableQuery.metrics,
      filter: errorTableQuery.filter,
      groupBy: errorTableQuery.groupBy,
      sortBy: errorTableQuery.sortBy,
      orderBy: errorTableQuery.orderBy,
    };

    const dimensionTablesPayload = get(errorDashboardStore, 'dimensionTables.requests');
    const dimensionNames = get(errorDashboardStore, 'dimensionTables.core.dimensions');
    const errorCode = get(errorDashboardStore, 'base.currentErrorCode');
    const activeMetric = get(errorDashboardStore, 'base.metric');
    const dimensionTableQueries = dimensionTablesPayload.map((payload, index) => {
      const query = get(payload, 'payload.queries.0');
      return {
        interval: query.interval,
        granularity: query.granularity,
        metrics: query.metrics,
        filter: query.filter,
        groupBy: query.groupBy,
        sortBy: query.sortBy,
        orderBy: query.orderBy,
        dimensionName: dimensionNames[index],
      };
    });
    return {
      dataset: DATASET,
      errorTable: {
        queryType: 'group-by',
        queries: errorTableMeta,
      },
      dimensionTables: {
        visible: true,
        queryType: 'group-by',
        queries: dimensionTableQueries,
      },
      widget: 'error_top_trends_error_table',
      errorCode,
      activeMetric,
    };
  },
);

export const errorSessionsMetaSelector = createSelector(
  errorDashboardSelector,
  viewerSessionsSelector,
  (errorDashboardStore, viewerSessionsStore) => {
    const errorTableQuery = get(errorDashboardStore, 'errorTable.payload.queries.0');
    const viewerSessionQuery = get(viewerSessionsStore, 'trends.query');
    const errorCode = get(errorDashboardStore, 'base.currentErrorCode');
    const errorTableMeta = {
      interval: errorTableQuery.interval,
      granularity: errorTableQuery.granularity,
      metrics: errorTableQuery.metrics,
      filter: errorTableQuery.filter,
      groupBy: errorTableQuery.groupBy,
      sortBy: errorTableQuery.sortBy,
      orderBy: errorTableQuery.orderBy,
    };
    return {
      dataset: DATASET,
      viewerSessions: {
        visible: true,
        queryType: 'scan',
        queries: viewerSessionQuery,
      },
      errorTable: {
        queryType: 'group-by',
        queries: errorTableMeta,
      },
      widget: 'error_sessions_error_table',
      errorCode,
    };
  },
);
